import { useMemo } from 'react'
import { navigate } from '../../lib/router'
import { Wordmark } from '../brand/Wordmark'

/* Deterministic PRNG so the sky is identical on every render and every
   visitor: no hydration drift, no flicker on remount. */
function mulberry32(seed: number) {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

type Star = { x: number; y: number; r: number; o: number; cls: string; dur: number; delay: number }

function makeSky(): { sparse: Star[]; accent: Star[] } {
  const rand = mulberry32(20260924)
  const twinkles = ['twinkle-a', 'twinkle-b', 'twinkle-c']
  const star = (rMin: number, rMax: number, oMin: number, oMax: number): Star => ({
    x: rand() * 1000,
    y: rand() * 1000,
    r: rMin + rand() * (rMax - rMin),
    o: oMin + rand() * (oMax - oMin),
    cls: twinkles[Math.floor(rand() * twinkles.length)] ?? 'twinkle-a',
    dur: 4 + rand() * 8,
    delay: -rand() * 12,
  })
  const sparse = Array.from({ length: 620 }, () => star(0.5, 1.3, 0.18, 0.6))
  const accent = Array.from({ length: 28 }, () => star(1.6, 2.6, 0.7, 1))
  return { sparse, accent }
}

/**
 * Black brand panel for the auth pages (doWallet login DNA): a starry
 * night, the wordmark in light tone, a two-line statement at the bottom.
 * No volt on the panel.
 */
export function BrandPanel({ line1, line2 }: { line1: string; line2: string }) {
  const { sparse, accent } = useMemo(makeSky, [])
  return (
    <aside className="hidden lg:flex lg:w-[44%] lg:p-4">
      <div className="relative flex flex-1 flex-col justify-between overflow-hidden rounded-xl bg-black p-8">
        <svg
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full"
          viewBox="0 0 1000 1000"
          preserveAspectRatio="xMidYMid slice"
        >
          {sparse.map((s, i) => (
            <circle key={`s${i}`} cx={s.x} cy={s.y} r={s.r} fill="white" opacity={s.o} />
          ))}
          {accent.map((s, i) => (
            <circle
              key={`a${i}`}
              cx={s.x}
              cy={s.y}
              r={s.r}
              fill="white"
              opacity={s.o}
              className={`twinkle ${s.cls}`}
              style={{ animationDuration: `${s.dur.toFixed(2)}s`, animationDelay: `${s.delay.toFixed(2)}s` }}
            />
          ))}
        </svg>
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ background: 'radial-gradient(circle at 28% 18%, rgba(255,255,255,0.08), transparent 55%)' }}
        />

        <a
          href="/"
          onClick={(e) => { e.preventDefault(); navigate('/') }}
          className="relative z-10 inline-flex w-fit transition-opacity hover:opacity-70"
          aria-label="Doable. Start page"
        >
          <Wordmark tone="light" />
        </a>

        <div className="relative z-10 space-y-6">
          <p className="max-w-md text-3xl font-normal leading-[1.05] tracking-tight text-white">
            <span className="block">{line1}</span>
            <span className="block text-white/60">{line2}</span>
          </p>
          <p className="text-xs text-white/40">© {new Date().getFullYear()} Applox GmbH · Made in Germany</p>
        </div>
      </div>
    </aside>
  )
}
